import { useEffect, useRef } from "react";
import { subscribe } from "../api/realtime";

// Keeps some data fresh: runs `refresh` now, whenever the server pushes
// `event`, when the stream (re)connects, when the tab becomes visible again,
// and as a slow fallback poll (only while the tab is visible) in case the
// stream is unavailable, for example behind a proxy that blocks streaming.
export default function useRealtimeRefresh(event, refresh, { enabled = true, fallbackMs = 120000, immediate = true } = {}) {
  const ref = useRef(refresh);
  ref.current = refresh;

  useEffect(() => {
    if (!enabled) return undefined;
    // Several triggers can fire together (reconnect, "ready", "resume");
    // coalesce them into one read.
    // A suppressed trigger still schedules one trailing read, so a push that
    // lands right after another refresh is never lost.
    let last = 0;
    let pending = null;
    const run = () => {
      const wait = 500 - (Date.now() - last);
      if (wait > 0) {
        if (!pending) {
          pending = setTimeout(() => {
            pending = null;
            run();
          }, wait);
        }
        return;
      }
      last = Date.now();
      if (ref.current) ref.current();
    };
    if (immediate) run();
    const offs = [subscribe(event, run), subscribe("ready", run), subscribe("resume", run)];
    const id = fallbackMs
      ? setInterval(() => {
          if (!document.hidden) run();
        }, fallbackMs)
      : null;
    return () => {
      offs.forEach((off) => off());
      if (id) clearInterval(id);
      if (pending) clearTimeout(pending);
    };
  }, [enabled, event, fallbackMs, immediate]);
}
